import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import PolicyContent from "./PolicyContent";

// Mandatory consent gate shown before creating a plan or downloading a PDF.
export default function PolicyConsentDialog({ open, onOpenChange, onAccept, loading = false, actionLabel = "Agree & Continue" }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => { if (open) setChecked(false); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="policy-consent-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-teal-400" /> Before you continue
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            SwotKit is a brainstorming tool. Please review and accept our Privacy Policy &amp; Terms of Use to continue.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-56 rounded-xl border border-slate-800 bg-slate-950/50 p-4 pr-5">
          <PolicyContent compact />
        </ScrollArea>

        <label data-testid="policy-consent-checkbox-label" className="flex items-start gap-3 cursor-pointer mt-1">
          <Checkbox
            data-testid="policy-consent-checkbox"
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            className="mt-0.5 border-slate-600 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
          />
          <span className="text-sm text-slate-300 leading-relaxed">
            I have read and agree to the{" "}
            <Link to="/privacy" target="_blank" className="text-teal-300 underline">Privacy Policy &amp; Terms of Use</Link>,
            and I understand SwotKit accepts no responsibility for the success or failure of any project based on its reports.
          </span>
        </label>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="hover:bg-slate-800">Cancel</Button>
          <Button
            data-testid="policy-consent-accept-button"
            disabled={!checked || loading}
            onClick={onAccept}
            className="cta-btn"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Please wait…</> : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
